<template>
  <div>
    <!-- Site-wide backdrop. Lives here rather than inside a page because it is
         position: fixed, which resolves against a transformed ancestor — and
         the horizontal track is permanently translated. -->
    <HoneycombField />
    <div id="smooth-wrapper">
      <div id="smooth-content">
        <NuxtPage />
      </div>
    </div>
    <ScrollProgress v-if="route.path !== '/explore'" />

    <!-- One for the whole app, driven from useConfirm. Every "are you sure"
         anywhere on the site is this dialog, rather than each list carrying
         its own copy of the same three buttons. -->
    <ConfirmDialog />
  </div>
</template>

<script setup>
// Smooth scroll (Lenis) lives in plugins/lenis.client.js so nav clicks
// anywhere in the app can call useLenis().scrollTo(...) without fighting it.
const route = useRoute();
</script>

<style lang="scss">
// Orbitron is loaded by the <link> in nuxt.config. It used to be pulled in a
// second time here through a stylesheet @import, which is both a duplicate
// request and the slower of the two ways to ask for it.

:root {
  --primary: #0f172a;
  --primary-rgb: 15, 23, 42;
  --secondary: #64748b;
  --secondary-rgb: 100, 116, 139;
  --accent: #3b82f6;
  --accent-rgb: 59, 130, 246;
  --background: #ffffff;
  --background-rgb: 255, 255, 255;
  --text: #1e293b;
  --text-rgb: 30, 41, 59;
  --muted: #94a3b8;
  --muted-rgb: 148, 163, 184;
  --border: #e2e8f0;
  --border-rgb: 226, 232, 240;
  --radius: 0.5rem;

  &.dark {
    --primary: #e2e8f0;
    --primary-rgb: 226, 232, 240;
    --secondary: #94a3b8;
    --secondary-rgb: 148, 163, 184;
    --accent: #3b82f6;
    --accent-rgb: 59, 130, 246;
    --background: #0f172a;
    --background-rgb: 15, 23, 42;
    --text: #f8fafc;
    --text-rgb: 248, 250, 252;
    --muted: #64748b;
    --muted-rgb: 100, 116, 139;
    --border: #1e293b;
    --border-rgb: 30, 41, 59;
  }
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

// Text is not selectable by default, and the exceptions are listed rather than
// the other way round.
//
// Be clear about what this is: a deterrent, not a lock. Every word is still in
// the page source and one devtools tab away, and it always will be, because the
// browser has to be given the text in order to draw it. This raises the effort
// of lifting a prompt or a backstory from "drag and Ctrl+C" to "go and look for
// it", which is worth something, and it is not worth mistaking for protection.
body {
  -webkit-user-select: none;
  -moz-user-select: none;
  user-select: none;
}

// The exceptions. Anything somebody legitimately needs to take away with them:
// an address to write to, a handle to search for, whatever they have typed
// themselves. Form controls are here because selection is not decoration in a
// field, it is how you edit.
input,
textarea,
select,
[contenteditable='true'],
.selectable,
.selectable * {
  -webkit-user-select: text;
  -moz-user-select: text;
  user-select: text;
}

// iOS zooms the page in when a field takes focus and its text is under 16px,
// and it does not zoom back out afterwards. Every form here is meant to be
// 16px already; this is the net that catches the next one that is not.
//
// !important because component styles are scoped, and a scoped `input[data-v-x]`
// outranks a bare `input` selector. There is no way to win this on specificity
// from here, and a rule that silently loses would be worse than no rule.
//
// Not a design constraint: 16px is the right size for a touch field anyway.
@media (max-width: 768px) {
  input,
  textarea,
  select {
    font-size: 16px !important;
  }
}

// Rendered Markdown, wherever it appears.
//
// Global rather than scoped to each page that renders some, because the markup
// arrives through v-html and scoped styles do not reach inside it. It also
// means the editor's preview and the live page are styled by one rule, so the
// preview cannot flatter the result.
.prose {
  // Selection stays off, as everywhere else on the public side; the editor
  // switches it back on for the whole admin.
  > :first-child { margin-top: 0; }
  > :last-child { margin-bottom: 0; }

  p {
    margin: 0 0 0.9em;
    line-height: 1.75;
  }

  strong { font-weight: 700; color: var(--text); }
  em { font-style: italic; }

  // A quote should read as somebody else's voice before it is read at all.
  //
  // The rule alone was not doing that: an accent-coloured line beside text set
  // in the same face and size as the paragraph above it is a margin note, not a
  // quotation. Three things change together here, and they have to be all three
  // or none: the typeface, the size, and the room around it.
  blockquote {
    margin: 1.6em 0;
    // Generous, and asymmetric on purpose. The indent is what separates the
    // passage from the column; padding on the right as well would centre it and
    // turn it into a pull quote, which is a different thing.
    padding: 0.1em 0 0.1em 1.5em;
    // Neutral rather than accent. The line is punctuation, and the eye should
    // land on the words rather than on a coloured bar.
    border-left: 3px solid rgba(var(--text-rgb, 17, 24, 39), 0.28);
    color: var(--text);
    // A serif against the page's sans, which is the whole of the tonal shift.
    // System faces rather than a download: a quote is not worth a font request,
    // and every platform has a decent one of these.
    font-family: Georgia, 'Iowan Old Style', 'Times New Roman', serif;
    font-style: italic;
    font-size: 1.06em;
    line-height: 1.7;

    p { margin-bottom: 0.6em; }
    > :last-child { margin-bottom: 0; }

    // Inside a quote the emphasis has to go the other way, or it disappears
    // into the italic around it.
    em { font-style: normal; }

    // Code keeps its own face; a monospace serif is not a thing worth having.
    code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-style: normal; }
  }

  h3, h4, h5, h6 {
    margin: 1.4em 0 0.5em;
    font-weight: 700;
    line-height: 1.3;
    color: var(--text);
  }

  h3 { font-size: 1.08em; }
  h4 { font-size: 1em; }
  h5, h6 { font-size: 0.95em; }

  ul, ol {
    margin: 0 0 0.9em;
    padding-left: 1.3em;
  }

  li {
    margin-bottom: 0.35em;
    line-height: 1.7;
  }

  code {
    padding: 0.1em 0.35em;
    border-radius: 5px;
    background: rgba(var(--muted-rgb), 0.14);
    font-family: 'Fira Code', 'SFMono-Regular', Consolas, monospace;
    font-size: 0.88em;
  }

  hr {
    margin: 1.5em 0;
    border: none;
    border-top: 1px solid rgba(var(--border-rgb), 0.6);
  }

  a {
    color: var(--accent);
    text-decoration: underline;
    text-underline-offset: 2px;
  }
}

// A shimmering placeholder in the shape of the thing being waited for.
//
// Content edited from /admin is read at request time, so on a slow connection
// these panels sat empty and then filled without warning. A block that says
// "something belongs here" reads as loading; a gap reads as nothing to show.
.skeleton {
  position: relative;
  overflow: hidden;
  border-radius: 6px;
  background: rgba(var(--muted-rgb), 0.14);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      90deg,
      rgba(var(--muted-rgb), 0) 0%,
      rgba(var(--muted-rgb), 0.16) 50%,
      rgba(var(--muted-rgb), 0) 100%
    );
    animation: skeleton-sweep 1.4s ease-in-out infinite;
  }
}

@keyframes skeleton-sweep {
  to { transform: translateX(100%); }
}

// A sweep crossing the screen every 1.4 seconds is exactly the kind of movement
// that causes trouble. The placeholder stays, the animation goes.
@media (prefers-reduced-motion: reduce) {
  .skeleton::after {
    animation: none;
  }
}

// The page background lives on <html> alone, deliberately. When <body> paints
// one too it covers any z-index: -1 backdrop (HoneycombField): in the root
// stacking context, negative-z-index children paint at step 2 but an in-flow
// body background paints at step 3, on top of them.
html {
  background-color: var(--background);
}

html, body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
  color: var(--text);
  // `clip`, not `hidden`, and the difference is the whole point.
  //
  // `overflow: hidden` makes an element a scroll container: it is still
  // scrollable, just not by the user. On html and body that turns the root
  // into something Chrome on Android will not apply its address-bar hiding
  // optimisation to cleanly, and the toolbar's show/hide transition ends up
  // fighting the page. That is the stutter at the start of every scroll.
  //
  // `clip` crops exactly the same overflow without creating a scroll
  // container at all, so the root scroller stays the plain one the browser
  // knows how to animate its own chrome against.
  overflow-x: hidden;

  // Native scrollbar is replaced by <ScrollProgress /> — the bottom bar. The
  // page still scrolls normally; only the visual scrollbar is suppressed.
  scrollbar-width: none;        // Firefox
  -ms-overflow-style: none;     // legacy Edge/IE
}

// A page that is one screen and is not scrolled. Explore puts this on the root
// on a phone and takes it off on the way out; nothing else uses it.
//
// Three things, because no one of them is enough on iOS. `overflow: hidden`
// and a fixed height leave the document with nowhere to go.
// `overscroll-behavior: none` is what stops the rubber band, and Safari only
// learned it in 16. Fixing the body to the viewport is the part that works
// before then, and it is safe here only because the page is locked from the
// moment it mounts, so there is no scroll position to lose.
//
// A plain fixed ancestor does not become the containing block for fixed
// descendants, so the navigation, the actions and the tabs still answer to the
// viewport. And this closes the document's scrolling, not every scroller on
// the page: the rail inside keeps swiping side to side.
// svh and not the layout viewport, so this agrees with the `100svh` the gallery
// inside is already sized to. They have to be the same number or one shows a
// strip of the other. It is also stable here in a way it is not elsewhere:
// nothing can scroll, so the address bar never retracts, so the small viewport
// is the viewport.
html.is-viewport-locked {
  height: 100vh;
  height: 100svh;
  overflow: hidden;
  overscroll-behavior: none;

  body {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100vh;
    height: 100svh;
    overflow: hidden;
    overscroll-behavior: none;
  }
}

// The theme animates only once the first one has been applied.
//
// This used to sit on `html, body` unconditionally, which meant the class the
// blocking script in nuxt.config adds was itself a transition: every load of
// the site in dark mode faded from white over 0.3s, repainting the whole
// viewport the entire time. `theme-ready` is added two frames in, by that same
// script, so the first paint is simply the right colour and every toggle after
// it still glides.
html.theme-ready,
html.theme-ready body {
  transition: background-color 0.3s ease, color 0.3s ease;
}

html::-webkit-scrollbar,
body::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;                // Chrome/Safari
}

#smooth-wrapper {
  width: 100%;
  position: relative;
  overflow: visible;
}

#smooth-content {
  width: 100%;
  // Every page is at least a screen tall, and this is which screen.
  //
  // On Chrome for Android 100vh is the LARGE viewport: the height the page has
  // once the address bar has retracted. At load the address bar is showing, so
  // what you can actually see is the small viewport, sixty to a hundred and
  // twenty pixels shorter. A page held to 100vh is therefore taller than the
  // screen the moment it appears, whatever is in it, so it can be scrolled,
  // so Chrome runs its browser-controls settle: the bar slides away, the
  // visual viewport grows, and everything on screen moves and stops.
  //
  // That movement is the drift, and it is only ever seen at the top because
  // Chrome's scroll anchoring hides shifts by adjusting the scroll offset, and
  // at offset zero there is nothing above the viewport to anchor to.
  //
  // svh is the small viewport, which is what is there at load, and unlike dvh
  // it is a fixed number that never tracks the bar. Explore has been the one
  // page immune to this all along, and it is the one page already sized in svh.
  //
  // vh first as the fallback: a browser without svh keeps exactly what it had.
  min-height: 100vh;
  min-height: 100svh;
}

.container {
  width: 100%;
  max-width: min(1400px, 88vw);
  margin: 0 auto;
  padding: 0 2rem;
}

// Horizontal-scroll page skeleton (desktop): a pinned viewport (.h-scroll-wrapper)
// containing a static background layer and a track of panels that GSAP
// ScrollTrigger translates horizontally as the user scrolls vertically.
.h-scroll-wrapper {
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

.h-scroll-track {
  position: relative;
  z-index: 1;
  display: flex;
  height: 100%;
  will-change: transform;
}

.h-scroll-panel {
  flex-shrink: 0;
  width: 100vw;
  height: 100%;
  overflow-y: auto;
  // Positioned so each panel forms its own stacking context above anything
  // decorative sitting behind the track.
  position: relative;
  z-index: 1;
}

.h-scroll-panel--intrinsic {
  width: auto;
}

@media (max-width: 768px) {
  // Mobile fallback: normal vertical stacking, no pin. `100%` rather than the
  // `100vw` above, which counts the scrollbar and overflows the page by it.
  .h-scroll-wrapper {
    width: 100%;
    height: auto;
    overflow: visible;
  }

  .h-scroll-track {
    display: block;
  }

  .h-scroll-panel,
  .h-scroll-panel--intrinsic {
    width: 100%;
    height: auto;
    overflow-y: visible;
  }
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 0.5rem;
}

h1 {
  font-size: clamp(2.5rem, 5vw, 5rem);
}

h2 {
  font-size: clamp(2rem, 4vw, 3.5rem);
}

h3 {
  font-size: clamp(1.5rem, 3vw, 2.5rem);
}

a {
  color: var(--accent);
  text-decoration: none;
  transition: color 0.3s ease;
  
  &:hover {
    text-decoration: underline;
  }
}

// Symmetric, and the 88vw cap stays.
//
// This did carry a 3.5rem clearance on the left, to keep the navigation dock
// off the first column of text below about 500px. It bought a seven pixel
// overlap with a translucent floating dock at the price of forty pixels of
// width and every section's balance: the hero, which overrides the padding
// here but not the cap, ran flat to the edge, and everything else sat with
// 56px on one side and 16px on the other. Visibly worse than the thing it
// fixed. See found_issues for the overlap, which is still there and is a
// design decision rather than a padding one.
@media (max-width: 768px) {
  .container {
    padding: 0 1rem;
  }
}

// Nested scrolling panels — a story column, a prompt body, an article modal.
//
// data-lenis-prevent is what makes the wheel work inside them: Lenis takes over
// wheel events for the whole document, so without it a panel could only be
// scrolled by dragging its bar. The attribute doubles as the hook for styling,
// since these are exactly the elements that need a visible bar of their own
// (the page's native one is suppressed).
[data-lenis-prevent] {
  scrollbar-width: thin;
  scrollbar-color: rgba(var(--muted-rgb), 0.45) transparent;

  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
    display: block;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(var(--muted-rgb), 0.35);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--accent-rgb), 0.55);
  }
}
</style>
